import React, { useState } from "react";
import { Plus, Pencil } from "lucide-react";
import { Chip, EmptyState, Loading, ErrorState } from "../ui/primitives.jsx";
import { AttachThumb, AttachViewer } from "../ui/attachments.jsx";
import { fmtMoney, fmtDate } from "../lib/format.js";
import { useMaintenance } from "./api.js";
import { useReports } from "./reports-api.js";
import MaintForm from "./MaintForm.jsx";
import ReportForm from "./ReportForm.jsx";

const TYPE_CHIP = { repair: "overdue", inspection: "reserved", service: "returned" };

export default function MaintenanceTab() {
  const [sub, setSub] = useState("log");
  const [editing, setEditing] = useState(null);
  const [editingReport, setEditingReport] = useState(null);
  const [viewing, setViewing] = useState(null);
  const maintenance = useMaintenance();
  const reports = useReports();

  return (
    <>
      <div className="subtabs">
        <button className={sub === "log" ? "on" : ""} onClick={() => setSub("log")}>
          Service log
        </button>
        <button className={sub === "reports" ? "on" : ""} onClick={() => setSub("reports")}>
          Condition reports (before / after)
        </button>
      </div>

      {sub === "log" && (
        <div className="card">
          <div className="card-h">
            <h2>Service &amp; repair log</h2>
            <div className="spacer" />
            <button className="btn primary" onClick={() => setEditing({})}>
              <Plus size={16} /> Log service
            </button>
          </div>
          <div className="card-b tight" style={{ overflowX: "auto" }}>
            {maintenance.isPending ? (
              <Loading what="service records" />
            ) : maintenance.error ? (
              <ErrorState error={maintenance.error} onRetry={() => maintenance.refetch()} />
            ) : maintenance.data.length === 0 ? (
              <EmptyState
                title="No service records"
                sub="Log oil changes, repairs, and inspections to build each machine's history."
                action={
                  <button className="btn primary" onClick={() => setEditing({})}>
                    <Plus size={16} /> Log service
                  </button>
                }
              />
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Machine</th>
                    <th>Type</th>
                    <th>Hours</th>
                    <th>Cost</th>
                    <th>Notes</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {maintenance.data.map((m) => (
                    <tr key={m.id}>
                      <td>{fmtDate(m.date)}</td>
                      <td className="main-cell">{m.equipment?.name ?? "—"}</td>
                      <td><Chip kind={TYPE_CHIP[m.type] ?? "reserved"}>{m.type}</Chip></td>
                      <td>{m.hours || "—"}</td>
                      <td
                        title={
                          `Parts ${fmtMoney(m.parts)} · Labor ${fmtMoney(m.labor)} · ` +
                          `Fuel ${fmtMoney(m.fuel)} · Other ${fmtMoney(m.other)}`
                        }
                      >
                        {Number(m.cost) ? fmtMoney(m.cost) : "—"}
                      </td>
                      <td style={{ maxWidth: 260 }}>{m.notes}</td>
                      <td style={{ textAlign: "right" }}>
                        <button className="iconbtn" title="Edit" onClick={() => setEditing(m)}>
                          <Pencil size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {sub === "reports" && (
        <div className="card">
          <div className="card-h">
            <h2>Condition reports</h2>
            <div className="spacer" />
            <button className="btn primary" onClick={() => setEditingReport({})}>
              <Plus size={16} /> New report
            </button>
          </div>
          <div className="card-b">
            <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 12 }}>
              Photograph every machine before it leaves and when it comes back. If there's
              damage, you'll have a dated record tied to the rental.
            </div>

            {reports.isPending ? (
              <Loading what="condition reports" />
            ) : reports.error ? (
              <ErrorState error={reports.error} onRetry={() => reports.refetch()} />
            ) : reports.data.length === 0 ? (
              <EmptyState
                title="No condition reports"
                sub="Snap before/after photos with notes for each rental."
              />
            ) : (
              reports.data.map((rp) => (
                <div
                  key={rp.id}
                  style={{ border: "1px solid var(--line)", borderRadius: 8, padding: "12px 14px", marginBottom: 12 }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <Chip kind={rp.phase === "before" ? "reserved" : "returned"}>
                      {rp.phase === "before" ? "Before rental" : "After return"}
                    </Chip>
                    <b>{rp.equipment?.name ?? "—"}</b>
                    <span className="sub-cell">
                      {fmtDate(rp.date)}
                      {rp.rentals?.customers?.name ? ` · ${rp.rentals.customers.name}` : ""}
                    </span>
                    <div style={{ marginLeft: "auto" }}>
                      <button className="iconbtn" title="Edit" onClick={() => setEditingReport(rp)}>
                        <Pencil size={16} />
                      </button>
                    </div>
                  </div>
                  {rp.notes && <div style={{ margin: "8px 0 4px", fontSize: 13.5 }}>{rp.notes}</div>}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                    {(rp.condition_photos ?? []).map((p) => (
                      <AttachThumb
                        key={p.id}
                        path={p.file_path}
                        size={70}
                        onClick={() => setViewing({ path: p.file_path })}
                      />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {editing !== null && <MaintForm rec={editing} onClose={() => setEditing(null)} />}
      {editingReport !== null && (
        <ReportForm rec={editingReport.id ? editingReport : null} onClose={() => setEditingReport(null)} />
      )}
      {viewing && <AttachViewer path={viewing.path} onClose={() => setViewing(null)} />}
    </>
  );
}
